import { Flex, FlexItem, Popover, TextInput } from '@patternfly/react-core'
import { OutlinedTimesCircleIcon } from '@patternfly/react-icons'
import * as React from 'react'
import { useCallback, useState } from 'react'

import { displayLabelIfExists } from '@app/domain/instrument/instrument.helpers'
import type {
  IProgramInteraction,
  IProgramInteractionId} from '@app/domain/program/program.interface';
import {
  ProgramInteractionTypeId,
  editInteractionInstrumentsModalTittle,
} from '@app/domain/program/program.interface'
import { ValidationStatusId } from '@app/lib/validators'
import { InfoPopoverContent } from '@app/utils/info-popover'
import { SmallButton } from '@app/utils/styles/small-button'

import { warningPopoverContent } from './messages'

interface FollowUpInteractionProps {
  programInteraction: IProgramInteraction
  onDeleteInteraction: (programIteractionId: IProgramInteractionId) => void
  onEditInteraction: (
    modalTittle: string,
    programInteractionTypeId: ProgramInteractionTypeId,
    programInteraction?: IProgramInteraction,
  ) => void
  onEditInteractionProperties: (
    interactionId: string,
    label?: string,
    daysAfterInitialInteraction?: number,
  ) => void
}

export const FollowUpInteraction = ({
  onDeleteInteraction,
  onEditInteraction,
  onEditInteractionProperties,
  programInteraction,
}: FollowUpInteractionProps) => {
  const [label, setLabel] = useState<string>(programInteraction.label)
  const [isPopoverVisible, setPopoverVisible] = useState(false)

  const [
    daysAfterInitialInteraction,
    setdaysAfterInitialInteraction,
  ] = useState<number>(programInteraction.daysAfterInitialInteraction)
  const [
    daysAfterInitialInteractionError,
    setdaysAfterInitialInteractionError,
  ] = useState<ValidationStatusId>(ValidationStatusId.Default)

  const handleDeleteInteraction = useCallback(() => {
    onDeleteInteraction(programInteraction.id)
  }, [onDeleteInteraction, programInteraction])

  const handleEditInteraction = useCallback(() => {
    onEditInteraction(
      editInteractionInstrumentsModalTittle,
      ProgramInteractionTypeId.FollowUpInteraction,
      programInteraction,
    )
  }, [onEditInteraction, programInteraction])

  const handleSetDaysAfterInitialInteraction = useCallback((textValue) => {
    const numberValue = Number.parseInt(textValue)
    if (!Number.isNaN(numberValue) && numberValue > 0) {
      setdaysAfterInitialInteraction(numberValue)
      setdaysAfterInitialInteractionError(ValidationStatusId.Default)
    } else {
      setdaysAfterInitialInteraction(undefined)
      setdaysAfterInitialInteractionError(ValidationStatusId.Error)
    }
  }, [])

  const handleBlur = useCallback(() => {
    onEditInteractionProperties(
      programInteraction.id,
      label,
      daysAfterInitialInteraction,
    )
  }, [
    daysAfterInitialInteraction,
    label,
    onEditInteractionProperties,
    programInteraction,
  ])

  const showPopover = useCallback(() => setPopoverVisible(true), [])
  const hidePopover = useCallback(() => setPopoverVisible(false), [])

  if (programInteraction.instruments.length > 0) {
    return (
      <>
        <Flex className="interaction-label-container">
          <FlexItem>
            <Popover
              aria-label="Delete Interaction Confirmation"
              isVisible={isPopoverVisible}
              shouldClose={hidePopover}
              bodyContent={
                <InfoPopoverContent
                  title="Danger"
                  body={warningPopoverContent}
                  isDanger={true}
                  onCancel={hidePopover}
                  onConfirm={handleDeleteInteraction}
                />
              }
            >
              <OutlinedTimesCircleIcon
                onClick={showPopover}
                className="link-image"
              />
            </Popover>
          </FlexItem>
          <FlexItem>
            <TextInput
              value={label ?? ''}
              isRequired
              type="text"
              id="label"
              name="label"
              onChange={setLabel}
              onBlur={handleBlur}
              placeholder="Label"
            />
          </FlexItem>
          <FlexItem>
            <TextInput
              value={daysAfterInitialInteraction}
              validated={daysAfterInitialInteractionError}
              isRequired
              type="text"
              id="days"
              name="days"
              onChange={handleSetDaysAfterInitialInteraction}
              onBlur={handleBlur}
              placeholder=""
              className={
                daysAfterInitialInteractionError === 'default'
                  ? 'text-input-narrow'
                  : 'text-input-wide'
              }
            />
            <span className="days-after-initial-interaction">
              days after initial interaction
            </span>
          </FlexItem>
        </Flex>
        <Flex>
          <FlexItem className="spacer" />
          <FlexItem className="label">Instruments:</FlexItem>
          <FlexItem>
            <SmallButton variant="secondary" onClick={handleEditInteraction}>
              Edit
            </SmallButton>
          </FlexItem>
          <FlexItem>
            {programInteraction.instruments
              ?.map(displayLabelIfExists)
              .join(', ')
              .toString()}
          </FlexItem>
        </Flex>
      </>
    )
  } else {
    return (
      <Flex>
        <FlexItem className="spacer" />
        <FlexItem className="label">Instruments:</FlexItem>
        <FlexItem />
        <FlexItem>
          <SmallButton variant="secondary" onClick={handleEditInteraction}>
            Edit
          </SmallButton>
        </FlexItem>
      </Flex>
    )
  }
}
